/**
 * Customer360Tabbed — Header + Tabs layout (Pattern 3).
 *
 * Full-width profile header with stats bar at top.
 * Tabs below: Overview, Cases, Shipments, Products, Devices.
 * Inspired by Wave, Customer.io, Jobber.
 */
import { useState } from 'react';
import { CaseLink, CustomerProfileLink } from './CaseLink';
import { CaseSummary } from './CaseSummary';
import type { CustomerProfile } from './Customer360.types';
import { getTrackingUrl } from './Customer360.viewmodel';
import {
  type AssetItem,
  type CaseItem,
  type MergedCustomer360,
  type ShipmentItem,
  toViewModel,
} from './Customer360.viewmodel';
import { DateTimeDisplay, formatDateTime } from './DateTimeDisplay';

// =============================================================================
// Helpers
// =============================================================================

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(d: string): string {
  // `new Date("YYYY-MM-DD")` parses as UTC midnight, then renders as the
  // previous day in any timezone west of UTC. Construct local midnight for
  // date-only inputs so the calendar date survives.
  let parsed: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    parsed = new Date(y, m - 1, day);
  } else {
    parsed = new Date(d);
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// `formatDateTime` is imported from DateTimeDisplay so all three layouts
// render timestamps identically.

function relativeDate(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** `Apr 14, 2026 · Today` — absolute date + relative in one string. */
function timeAgo(d: string): string {
  return `${formatDate(d)} · ${relativeDate(d)}`;
}

// =============================================================================
// Colors & styles
// =============================================================================

// Cloudscape-aligned palette — see Customer360Panel for the rationale.
const c = {
  border: '#ebebf0',
  borderLight: '#f2f3f3',
  bg: '#ffffff',
  bgMuted: '#f6f6f9',
  text: '#16191f',
  textSec: '#424650',
  textMuted: '#545b64',
  blue: '#006ce0',
  green: '#037f0c',
  red: '#d13212',
  amber: '#8c3b00',
  purple: '#8b5cf6',
};

const statusBadge: Record<string, { bg: string; fg: string }> = {
  open: { bg: '#fef3c7', fg: '#92400e' },
  closed: { bg: '#f3f4f6', fg: '#6b7280' },
  pending: { bg: '#dbeafe', fg: '#1e40af' },
  Delivered: { bg: '#dcfce7', fg: '#166534' },
  Shipped: { bg: '#dbeafe', fg: '#1e40af' },
  Processing: { bg: '#fef3c7', fg: '#92400e' },
  Active: { bg: '#dcfce7', fg: '#166534' },
  Warranty: { bg: '#ede9fe', fg: '#6d28d9' },
};

const s: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: c.bg,
    overflow: 'hidden',
  },
  // -- Header --
  header: {
    padding: '16px 24px',
    borderBottom: `1px solid ${c.border}`,
    flexShrink: 0,
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: c.blue,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 700,
    flexShrink: 0,
  },
  headerName: {
    fontSize: '18px',
    fontWeight: 600,
    color: c.text,
    margin: 0,
  },
  headerMeta: {
    fontSize: '13px',
    color: c.textSec,
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginTop: '2px',
  },
  statusDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  mergedBadge: {
    fontSize: '11px',
    color: c.textMuted,
    background: c.borderLight,
    padding: '2px 8px',
    borderRadius: '10px',
    marginLeft: 'auto',
  },
  // -- Stats bar --
  statsBar: {
    display: 'flex',
    gap: '0',
    marginTop: '14px',
    borderRadius: '8px',
    border: `1px solid ${c.border}`,
    overflow: 'hidden',
  },
  stat: {
    flex: 1,
    padding: '10px 16px',
    borderRight: `1px solid ${c.border}`,
    textAlign: 'center',
  },
  statLast: {
    borderRight: 'none',
  },
  statValue: {
    fontSize: '17px',
    fontWeight: 700,
    color: c.text,
  },
  statLabel: {
    fontSize: '10px',
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginTop: '1px',
  },
  // -- Alert --
  alert: {
    margin: '12px 24px 0',
    padding: '8px 14px',
    fontSize: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    lineHeight: 1.4,
  },
  // -- Tabs --
  tabBar: {
    display: 'flex',
    gap: '0',
    borderBottom: `1px solid ${c.border}`,
    padding: '0 24px',
    flexShrink: 0,
  },
  tab: {
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 500,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    color: c.textSec,
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  tabActive: {
    color: c.blue,
    borderBottomColor: c.blue,
    fontWeight: 600,
  },
  tabCount: {
    fontSize: '10px',
    fontWeight: 400,
    color: c.textMuted,
    background: c.borderLight,
    padding: '1px 6px',
    borderRadius: '8px',
  },
  // -- Content --
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '0',
  },
  // -- Table --
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 24px',
    fontWeight: 600,
    fontSize: '11px',
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    borderBottom: `1px solid ${c.border}`,
    background: c.bgMuted,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: '10px 24px',
    borderBottom: `1px solid ${c.borderLight}`,
    color: c.text,
    verticalAlign: 'top',
  },
  tdMuted: {
    color: c.textSec,
    fontSize: '12px',
  },
  // -- Expandable row --
  expandedRow: {
    background: c.bgMuted,
  },
  expandContent: {
    padding: '8px 24px 14px 24px',
    fontSize: '12px',
    color: c.textSec,
    lineHeight: 1.5,
  },
  // -- Badge --
  badge: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  },
  // -- Overview cards --
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    padding: '16px 24px',
  },
  overviewCard: {
    border: `1px solid ${c.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  overviewCardTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    padding: '10px 14px',
    borderBottom: `1px solid ${c.borderLight}`,
    background: c.bgMuted,
  },
  overviewCardBody: {
    padding: '10px 14px',
  },
  propRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: '12px',
  },
  propLabel: {
    color: c.textMuted,
  },
  propValue: {
    color: c.text,
    fontWeight: 500,
    textAlign: 'right',
    maxWidth: '60%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const alertColors: Record<string, React.CSSProperties> = {
  warning: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' },
  error: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' },
  info: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
};

// =============================================================================
// Sub-components
// =============================================================================

function Badge({ text }: { text: string }) {
  const colors = statusBadge[text] || { bg: '#f3f4f6', fg: '#6b7280' };
  return <span style={{ ...s.badge, background: colors.bg, color: colors.fg }}>{text}</span>;
}

// -- Overview tab --
function OverviewTab({ data }: { data: MergedCustomer360 }) {
  const id = data.identity;
  const recentCases = data.cases.slice(0, 3);
  const recentShipments = data.shipments.slice(0, 3);

  return (
    <div style={s.overviewGrid}>
      {/* Contact details */}
      <div style={s.overviewCard}>
        <div style={s.overviewCardTitle}>Contact Details</div>
        <div style={s.overviewCardBody}>
          {id.emails.map((e, i) => (
            <div key={i} style={s.propRow}>
              <span style={s.propLabel}>Email</span>
              <span style={s.propValue}>{e}</span>
            </div>
          ))}
          {id.phones.map((p, i) => (
            <div key={i} style={s.propRow}>
              <span style={s.propLabel}>Phone</span>
              <span style={s.propValue}>{p}</span>
            </div>
          ))}
          {id.address && (
            <div style={s.propRow}>
              <span style={s.propLabel}>Address</span>
              <span style={s.propValue} title={id.address}>
                {id.address}
              </span>
            </div>
          )}
          {id.memberSince && (
            <div style={s.propRow}>
              <span style={s.propLabel}>Member since</span>
              <span style={s.propValue}>{id.memberSince}</span>
            </div>
          )}
        </div>
      </div>

      {/* Account info */}
      <div style={s.overviewCard}>
        <div style={s.overviewCardTitle}>Account</div>
        <div style={s.overviewCardBody}>
          {id.sfAccountId && (
            <div style={s.propRow}>
              <span style={s.propLabel}>SF Account</span>
              <span style={{ ...s.propValue, fontFamily: 'monospace', fontSize: '10px' }}>
                {id.sfAccountId}
              </span>
            </div>
          )}
          <div style={s.propRow}>
            <span style={s.propLabel}>Type</span>
            <span style={s.propValue}>{id.accountType || '—'}</span>
          </div>
          <div style={s.propRow}>
            <span style={s.propLabel}>Status</span>
            <span style={s.propValue}>{id.accountStatus || '—'}</span>
          </div>
          {data.sourceProfiles.length > 1 && (
            <div style={{ marginTop: '8px' }}>
              <div
                style={{
                  fontSize: '10px',
                  color: c.textMuted,
                  fontWeight: 600,
                  marginBottom: '4px',
                }}
              >
                MERGED FROM {data.sourceProfiles.length} PROFILES
              </div>
              {data.sourceProfiles.map((sp) => (
                <div
                  key={sp.profileId}
                  style={{ fontSize: '11px', color: c.textMuted, padding: '1px 0' }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      background: c.blue,
                      marginRight: '5px',
                      verticalAlign: 'middle',
                    }}
                  />
                  <CustomerProfileLink profileId={sp.profileId}>
                    {sp.email || sp.phone || sp.profileId.slice(0, 8)}
                  </CustomerProfileLink>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent cases */}
      {recentCases.length > 0 && (
        <div style={s.overviewCard}>
          <div style={s.overviewCardTitle}>Recent Cases</div>
          <div style={s.overviewCardBody}>
            {recentCases.map((cs) => (
              <div
                key={cs.caseId}
                style={{ padding: '4px 0', borderBottom: `1px solid ${c.borderLight}` }}
              >
                <div style={{ fontSize: '12px', fontWeight: 500, color: c.text }}>{cs.title}</div>
                <div
                  style={{
                    fontSize: '11px',
                    color: c.textMuted,
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                    marginTop: '1px',
                  }}
                >
                  <Badge text={cs.status} />
                  <span>{timeAgo(cs.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent shipments */}
      {recentShipments.length > 0 && (
        <div style={s.overviewCard}>
          <div style={s.overviewCardTitle}>Recent Shipments</div>
          <div style={s.overviewCardBody}>
            {recentShipments.map((sh, i) => (
              <div key={i} style={{ padding: '3px 0', fontSize: '12px' }}>
                <span style={{ color: c.text }}>{sh.productDescription}</span>
                <span style={{ color: c.textMuted, marginLeft: '6px' }}>{sh.productSku}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -- Cases tab --
function CasesTab({
  cases,
  selectedId,
  onSelect,
}: {
  cases: CaseItem[];
  selectedId?: string;
  onSelect: (d: DetailItem) => void;
}) {
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>Title</th>
          <th style={s.th}>Ref #</th>
          <th style={s.th}>Status</th>
          <th style={s.th}>Brand</th>
          <th style={s.th}>Created</th>
        </tr>
      </thead>
      <tbody>
        {cases.map((cs) => (
          <tr
            key={cs.caseId}
            onClick={() =>
              onSelect({
                id: cs.caseId,
                caseId: cs.caseId,
                type: 'Case',
                title: cs.title,
                timestamp: cs.createdAt,
                summary: cs.summary,
                parts: cs.productsOrdered?.split(';').map((p) => p.trim()),
                fields: [
                  { label: 'Ref #', value: cs.referenceNumber },
                  { label: 'Status', value: cs.status },
                  ...(cs.brand ? [{ label: 'Brand', value: cs.brand }] : []),
                  ...(cs.asset ? [{ label: 'Product', value: cs.asset }] : []),
                  { label: 'Created', value: formatDateTime(cs.createdAt) },
                  ...(cs.lastUpdated
                    ? [{ label: 'Updated', value: formatDateTime(cs.lastUpdated) }]
                    : []),
                  ...(cs.agentName ? [{ label: 'Agent', value: cs.agentName }] : []),
                  ...(cs.bpo ? [{ label: 'BPO', value: cs.bpo }] : []),
                  ...(cs.site ? [{ label: 'Site', value: cs.site }] : []),
                  ...(cs.loupeLink
                    ? [{ label: 'Loupe', value: 'Open scan results', href: cs.loupeLink }]
                    : []),
                  { label: 'Case ID', value: cs.caseId.slice(0, 20) + '...', mono: true },
                ],
              })
            }
            style={{ cursor: 'pointer', ...(selectedId === cs.caseId ? s.expandedRow : {}) }}
          >
            <td style={{ ...s.td, fontWeight: 500 }}>{cs.title}</td>
            <td style={{ ...s.td, ...s.tdMuted, whiteSpace: 'nowrap' }}>
              <CaseLink caseId={cs.caseId}>#{cs.referenceNumber}</CaseLink>
            </td>
            <td style={s.td}>
              <Badge text={cs.status} />
            </td>
            <td style={{ ...s.td, ...s.tdMuted }}>{cs.brand || '—'}</td>
            <td style={{ ...s.td, ...s.tdMuted, whiteSpace: 'nowrap' }}>{timeAgo(cs.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// -- Shipments tab --
function ShipmentsTab({
  shipments,
  selectedId,
  onSelect,
}: {
  shipments: ShipmentItem[];
  selectedId?: string;
  onSelect: (d: DetailItem) => void;
}) {
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>Product</th>
          <th style={s.th}>SKU</th>
          <th style={s.th}>Order #</th>
          <th style={s.th}>Date</th>
          <th style={s.th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {shipments.map((sh, i) => {
          const id = `${sh.orderNumber}-${sh.productSku}`;
          // Mirror the Search List logic: is_delivered → ship_date → pending.
          // tracking_number is unreliable — sometimes empty even after
          // shipment (carrier wrote it to a header field instead).
          const status = sh.isDelivered ? 'Delivered' : sh.shipDate ? 'Shipped' : 'Pending';
          return (
            <tr
              key={i}
              style={{ cursor: 'pointer', ...(selectedId === id ? s.expandedRow : {}) }}
              onClick={() =>
                onSelect({
                  id,
                  type: 'Shipment',
                  title: sh.productDescription,
                  timestamp: sh.orderDate,
                  fields: [
                    { label: 'SKU', value: sh.productSku, mono: true },
                    { label: 'Order #', value: String(sh.orderNumber) },
                    { label: 'Date', value: formatDateTime(sh.orderDate) },
                    { label: 'Qty', value: sh.quantity || '1' },
                    { label: 'Status', value: status },
                    ...(sh.trackingNumber
                      ? [
                          {
                            label: 'Tracking',
                            value: sh.trackingNumber,
                            mono: true,
                            href: getTrackingUrl(sh.shipperName, sh.trackingNumber) ?? undefined,
                          },
                        ]
                      : []),
                  ],
                })
              }
            >
              <td style={{ ...s.td, fontWeight: 500 }}>{sh.productDescription}</td>
              <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px', color: c.textSec }}>
                {sh.productSku}
              </td>
              <td style={{ ...s.td, ...s.tdMuted }}>{sh.orderNumber}</td>
              <td style={{ ...s.td, ...s.tdMuted, whiteSpace: 'nowrap' }}>
                {formatDate(sh.orderDate)}
              </td>
              <td style={s.td}>
                <Badge text={status} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// -- Products tab --
function ProductsTab({
  assets,
  selectedId,
  onSelect,
}: {
  assets: AssetItem[];
  selectedId?: string;
  onSelect: (d: DetailItem) => void;
}) {
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>Product</th>
          <th style={s.th}>Serial Number</th>
          <th style={s.th}>Family</th>
          <th style={s.th}>Registered</th>
          <th style={s.th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {assets.map((a) => (
          <tr
            key={a.serialNumber}
            style={{ cursor: 'pointer', ...(selectedId === a.serialNumber ? s.expandedRow : {}) }}
            onClick={() =>
              onSelect({
                id: a.serialNumber,
                type: 'Product',
                title: a.productName,
                fields: [
                  { label: 'Serial', value: a.serialNumber, mono: true },
                  ...(a.productFamily ? [{ label: 'Family', value: a.productFamily }] : []),
                  ...(a.registeredDate
                    ? [{ label: 'Registered', value: formatDateTime(a.registeredDate) }]
                    : []),
                  { label: 'Status', value: a.status },
                ],
              })
            }
          >
            <td style={{ ...s.td, fontWeight: 500 }}>
              {a.productName}
              {a.nickname && (
                <span style={{ fontWeight: 400, color: c.textMuted, marginLeft: '6px' }}>
                  — {a.nickname}
                </span>
              )}
            </td>
            <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px', color: c.textSec }}>
              {a.serialNumber}
            </td>
            <td style={{ ...s.td, ...s.tdMuted }}>{a.productFamily || '—'}</td>
            <td style={{ ...s.td, ...s.tdMuted, whiteSpace: 'nowrap' }}>
              {a.registeredDate ? formatDate(a.registeredDate) : '—'}
            </td>
            <td style={s.td}>
              <Badge text={a.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// =============================================================================
// Main Component
// =============================================================================

// =============================================================================
// Timeline Tab
// =============================================================================

interface TimelineEvent {
  id: string;
  type: 'case' | 'shipment' | 'device' | 'order';
  title: string;
  timestamp: string;
  status?: string;
  brand?: string;
  summary?: string;
  detail: Record<string, unknown>;
}

const eventDotColors: Record<string, string> = {
  case: '#3b82f6',
  shipment: '#8b5cf6',
  order: '#22c55e',
  device: '#f59e0b',
};
const eventTypeLabels: Record<string, string> = {
  case: 'Case',
  shipment: 'Shipment',
  order: 'Order',
  device: 'Device',
};

function buildTimeline(data: MergedCustomer360): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  data.cases.forEach((cs) =>
    events.push({
      id: cs.caseId,
      type: 'case',
      title: cs.title,
      timestamp: cs.createdAt,
      status: cs.status,
      brand: cs.brand,
      summary: cs.summary,
      detail: cs as unknown as Record<string, unknown>,
    })
  );
  const shipGroups = new Map<string, ShipmentItem[]>();
  data.shipments.forEach((sh) => {
    const k = String(sh.orderNumber);
    if (!shipGroups.has(k)) shipGroups.set(k, []);
    shipGroups.get(k)!.push(sh);
  });
  shipGroups.forEach((items, num) =>
    events.push({
      id: `shipment-${num}`,
      type: 'shipment',
      title: `Shipment #${num} — ${items.length} item${items.length > 1 ? 's' : ''}`,
      timestamp: items[0].orderDate,
      detail: { orderNumber: num, items } as unknown as Record<string, unknown>,
    })
  );
  data.orders.forEach((o) =>
    events.push({
      id: o.orderNumber,
      type: 'order',
      title: `Order ${o.orderNumber}`,
      timestamp: o.date,
      status: o.status,
      detail: o as unknown as Record<string, unknown>,
    })
  );
  data.connectedDevices.forEach((d) => {
    if (d.lastConnected)
      events.push({
        id: d.controllerSerial,
        type: 'device',
        title: `${d.tradeName} connected`,
        timestamp: d.lastConnected,
        detail: d as unknown as Record<string, unknown>,
      });
  });
  events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return events;
}

function TimelineTab({
  data,
  selectedId,
  onSelect,
}: {
  data: MergedCustomer360;
  selectedId?: string;
  onSelect: (d: DetailItem) => void;
}) {
  const events = buildTimeline(data);
  const grouped = new Map<string, TimelineEvent[]>();
  events.forEach((e) => {
    const date = formatDate(e.timestamp);
    if (!grouped.has(date)) grouped.set(date, []);
    grouped.get(date)!.push(e);
  });

  return (
    <div>
      {Array.from(grouped.entries()).map(([date, dateEvents]) => (
        <div key={date}>
          <div
            style={{
              padding: '6px 24px',
              fontSize: '11px',
              fontWeight: 600,
              color: c.textMuted,
              background: c.bgMuted,
              borderBottom: `1px solid ${c.border}`,
            }}
          >
            {date}
          </div>
          {dateEvents.map((ev) => (
            <div
              key={ev.id}
              onClick={() =>
                onSelect({
                  id: ev.id,
                  caseId: ev.type === 'case' ? ev.id : undefined,
                  type: eventTypeLabels[ev.type],
                  title: ev.title,
                  timestamp: ev.timestamp,
                  summary: ev.summary,
                  parts: (ev.detail.productsOrdered as string)?.split(';').map((p) => p.trim()),
                  items:
                    ev.type === 'shipment' && Array.isArray(ev.detail.items)
                      ? (ev.detail.items as ShipmentItem[]).map(
                          (i) => `${i.productDescription} (${i.productSku})`
                        )
                      : undefined,
                  fields: [
                    ...(ev.brand ? [{ label: 'Brand', value: ev.brand }] : []),
                    ...(ev.status ? [{ label: 'Status', value: ev.status }] : []),
                    ...(ev.detail.referenceNumber
                      ? [{ label: 'Ref #', value: ev.detail.referenceNumber as string }]
                      : []),
                    ...(ev.detail.asset
                      ? [{ label: 'Product', value: ev.detail.asset as string }]
                      : []),
                    ...(ev.type === 'case' && ev.detail.agentName
                      ? [{ label: 'Agent', value: ev.detail.agentName as string }]
                      : []),
                    ...(ev.type === 'case' && ev.detail.bpo
                      ? [{ label: 'BPO', value: ev.detail.bpo as string }]
                      : []),
                    ...(ev.type === 'case' && ev.detail.site
                      ? [{ label: 'Site', value: ev.detail.site as string }]
                      : []),
                    ...(ev.type === 'case' && ev.detail.loupeLink
                      ? [
                          {
                            label: 'Loupe',
                            value: 'Open scan results',
                            href: ev.detail.loupeLink as string,
                          },
                        ]
                      : []),
                    { label: 'Date', value: formatDateTime(ev.timestamp) },
                    {
                      label: 'ID',
                      value: ev.id.length > 20 ? ev.id.slice(0, 20) + '...' : ev.id,
                      mono: true,
                    },
                  ],
                })
              }
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '8px 24px',
                cursor: 'pointer',
                borderBottom: `1px solid ${c.borderLight}`,
                background: selectedId === ev.id ? '#eff6ff' : 'transparent',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  color: c.textMuted,
                  width: '55px',
                  flexShrink: 0,
                  paddingTop: '2px',
                  fontFamily: 'monospace',
                }}
              >
                {new Date(ev.timestamp).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: eventDotColors[ev.type],
                  flexShrink: 0,
                  marginTop: '5px',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    color: c.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ev.title}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: c.textMuted,
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                    marginTop: '1px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '6px',
                      background: eventDotColors[ev.type] + '20',
                      color: eventDotColors[ev.type],
                    }}
                  >
                    {eventTypeLabels[ev.type]}
                  </span>
                  {ev.status && <Badge text={ev.status} />}
                  {ev.brand && <Badge text={`Brand: ${ev.brand}`} />}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Detail Panel (right side)
// =============================================================================

interface DetailItem {
  id: string;
  type: string;
  title: string;
  timestamp?: string;
  fields: { label: string; value: string; mono?: boolean; href?: string }[];
  summary?: string;
  parts?: string[];
  items?: string[];
  /** When set, the "Ref #" field renders as a Connect Cases deep-link. */
  caseId?: string;
}

function DetailPanel({ item }: { item: DetailItem | null }) {
  if (!item) {
    return (
      <div
        style={{
          width: '300px',
          flexShrink: 0,
          borderLeft: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: c.textMuted,
          fontSize: '13px',
          background: c.bgMuted,
        }}
      >
        Select an item to view details
      </div>
    );
  }

  return (
    <div
      style={{
        width: '300px',
        flexShrink: 0,
        borderLeft: `1px solid ${c.border}`,
        overflow: 'auto',
        background: c.bgMuted,
      }}
    >
      <div
        style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}`, background: c.bg }}
      >
        {item.timestamp && (
          <DateTimeDisplay
            value={item.timestamp}
            style={{ fontSize: '11px', color: c.textMuted, display: 'block' }}
          />
        )}
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: c.text,
            marginTop: '2px',
            lineHeight: 1.3,
          }}
        >
          {item.title}
        </div>
        <div style={{ marginTop: '4px', display: 'flex', gap: '6px' }}>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: '6px',
              background: c.borderLight,
              color: c.textSec,
            }}
          >
            {item.type}
          </span>
        </div>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {item.summary && (
          <div
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '10px 12px',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: c.textMuted,
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}
            >
              Summary
            </div>
            <CaseSummary source={item.summary} style={{ fontSize: '12px', color: c.textSec }} />
          </div>
        )}
        {item.parts && item.parts.length > 0 && (
          <div
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '10px 12px',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: c.textMuted,
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}
            >
              Parts Ordered
            </div>
            {item.parts.map((p, i) => (
              <div key={i} style={{ fontSize: '12px', color: c.textSec, padding: '1px 0' }}>
                • {p}
              </div>
            ))}
          </div>
        )}
        {item.items && item.items.length > 0 && (
          <div
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '10px 12px',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: c.textMuted,
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}
            >
              Items
            </div>
            {item.items.map((it, i) => (
              <div key={i} style={{ fontSize: '12px', color: c.textSec, padding: '1px 0' }}>
                • {it}
              </div>
            ))}
          </div>
        )}
        <div
          style={{
            background: c.bg,
            border: `1px solid ${c.border}`,
            borderRadius: '8px',
            padding: '10px 12px',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              fontWeight: 600,
              color: c.textMuted,
              textTransform: 'uppercase',
              marginBottom: '4px',
            }}
          >
            Properties
          </div>
          {item.fields.map((f, i) => {
            const linkable = item.caseId && f.label === 'Ref #';
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '3px 0',
                  fontSize: '12px',
                }}
              >
                <span style={{ color: c.textMuted }}>{f.label}</span>
                <span
                  style={{
                    color: c.text,
                    fontWeight: 500,
                    textAlign: 'right',
                    maxWidth: '55%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ...(f.mono ? { fontFamily: 'monospace', fontSize: '10px' } : {}),
                  }}
                >
                  {linkable ? (
                    <CaseLink caseId={item.caseId!}>{f.value}</CaseLink>
                  ) : f.href ? (
                    <a
                      href={f.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        color: '#2563eb',
                        textDecoration: 'underline',
                        textDecorationColor: '#93c5fd',
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '2px',
                        fontWeight: 500,
                      }}
                    >
                      {f.value}
                    </a>
                  ) : (
                    f.value
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

// Products subsumes Devices (matches Search List + viewmodel merge).
type Tab = 'overview' | 'timeline' | 'cases' | 'shipments' | 'products';

export function Customer360Tabbed({ data: profile }: { data: CustomerProfile }) {
  const data = toViewModel(profile);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [selectedDetail, setSelectedDetail] = useState<DetailItem | null>(null);
  const id = data.identity;
  const initials = id.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const timelineCount =
    data.cases.length + data.shipments.length + data.orders.length + data.connectedDevices.length;
  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'timeline', label: 'Timeline', count: timelineCount },
    { key: 'cases', label: 'Cases', count: data.cases.length },
    { key: 'shipments', label: 'Shipments', count: data.shipments.length },
    { key: 'products', label: 'Products', count: data.assets.length },
  ];

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTop}>
          <div style={s.avatar}>{initials}</div>
          <div style={{ flex: 1 }}>
            <h1 style={s.headerName}>
              {id.primaryProfileId ? (
                <CustomerProfileLink profileId={id.primaryProfileId} style={{ color: 'inherit' }}>
                  {id.name}
                </CustomerProfileLink>
              ) : (
                id.name
              )}
            </h1>
            <div style={s.headerMeta}>
              <span>{id.emails[0]}</span>
              <span>&middot;</span>
              <span>{id.phones[0]}</span>
              {id.accountStatus && (
                <>
                  <span>&middot;</span>
                  <span>
                    <span
                      style={{
                        ...s.statusDot,
                        background: id.accountStatus === 'Active' ? c.green : c.textMuted,
                      }}
                    />
                    {id.accountStatus}
                  </span>
                </>
              )}
            </div>
          </div>
          {data.sourceProfiles.length > 1 && (
            <span style={s.mergedBadge}>Merged from {data.sourceProfiles.length} profiles</span>
          )}
        </div>

        {/* Stats bar */}
        <div style={s.statsBar}>
          {data.stats.ytdSales !== undefined && (
            <div style={s.stat}>
              <div style={s.statValue}>{formatCurrency(data.stats.ytdSales)}</div>
              <div style={s.statLabel}>YTD Sales</div>
            </div>
          )}
          <div style={s.stat}>
            <div style={{ ...s.statValue, color: data.stats.openCases > 0 ? c.red : c.text }}>
              {data.stats.openCases}
            </div>
            <div style={s.statLabel}>Open Cases</div>
          </div>
          <div style={s.stat}>
            <div style={s.statValue}>{data.stats.totalCases}</div>
            <div style={s.statLabel}>Total Cases</div>
          </div>
          <div style={s.stat}>
            <div style={s.statValue}>{data.stats.totalOrders}</div>
            <div style={s.statLabel}>Orders</div>
          </div>
          <div style={{ ...s.stat, ...s.statLast }}>
            <div style={s.statValue}>{data.stats.registeredProducts}</div>
            <div style={s.statLabel}>Products</div>
          </div>
        </div>
      </div>

      {/* Alert */}
      {data.alert && (
        <div style={{ ...s.alert, ...(alertColors[data.alert.type] || alertColors.info) }}>
          {data.alert.type === 'warning' ? '⚠' : data.alert.type === 'error' ? '✕' : 'ℹ'}
          <span>{data.alert.message}</span>
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabBar}>
        {tabs
          .filter((t) => t.key === 'overview' || (t.count ?? 0) > 0)
          .map((tab) => (
            <button
              key={tab.key}
              style={{ ...s.tab, ...(activeTab === tab.key ? s.tabActive : {}) }}
              onClick={() => {
                setActiveTab(tab.key);
                setSelectedDetail(null);
              }}
            >
              {tab.label}
              {tab.count !== undefined && <span style={s.tabCount}>{tab.count}</span>}
            </button>
          ))}
      </div>

      {/* Content — 2-column: list + detail */}
      <div style={{ ...s.content, display: 'flex' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {activeTab === 'overview' && <OverviewTab data={data} />}
          {activeTab === 'timeline' && (
            <TimelineTab data={data} selectedId={selectedDetail?.id} onSelect={setSelectedDetail} />
          )}
          {activeTab === 'cases' && (
            <CasesTab
              cases={data.cases}
              selectedId={selectedDetail?.id}
              onSelect={setSelectedDetail}
            />
          )}
          {activeTab === 'shipments' && (
            <ShipmentsTab
              shipments={data.shipments}
              selectedId={selectedDetail?.id}
              onSelect={setSelectedDetail}
            />
          )}
          {activeTab === 'products' && (
            <ProductsTab
              assets={data.assets}
              selectedId={selectedDetail?.id}
              onSelect={setSelectedDetail}
            />
          )}
        </div>
        {activeTab !== 'overview' && <DetailPanel item={selectedDetail} />}
      </div>
    </div>
  );
}
